// src/auth/access.js
import type { NavItem } from "../types/UserTypes";

export const Roles: Record<string, number> = {
  Owner: 1,
  Admin: 2,
  ProjectManager: 3,
  User: 4,
};

/** Map roleId → human-readable label */
export const RoleLabels: Record<number, string> = {
  [Roles.Owner]: "Owner",
  [Roles.Admin]: "Admin",
  [Roles.ProjectManager]: "Project Manager",
  [Roles.User]: "User",
};

/** True for Owner (roleId=1) only */
export const isOwner = (roleId: number): boolean => roleId === Roles.Owner;

/** True for Owner or Admin (roleId ≤ 2) — the "administrator" check */
export const isAdmin = (roleId: number): boolean =>
  roleId === Roles.Owner || roleId === Roles.Admin;

/** True for Project Manager only */
export const isProjectManager = (roleId: number): boolean =>
  roleId === Roles.ProjectManager;


/**
 * Route-level access check
 * Used by RequireRole.jsx
 */
export const canAccess = (roleId: number, allowedRoles: number[]): boolean => {
  if (!roleId) return false;
  if (!Array.isArray(allowedRoles)) return false;
  return allowedRoles.includes(roleId);
};

/**
 * Navigation configuration
 */
export const NavItems: NavItem[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    to: "/dashboard",
    icon: "/images/dashboard-logo.svg",
    roles: [Roles.Owner, Roles.Admin],
    features: ["dashboard"],
  },
  {
    key: "clients",
    label: "Clients",
    to: "/clients",
    icon: "/images/Clients-logo.svg",
    roles: [Roles.Owner, Roles.Admin, Roles.ProjectManager],
    features: ["client_prospect_coding"],
  },
  {
    key: "prospects",
    label: "Prospects",
    to: "/prospects",
    icon: "/images/prospects-logo.svg",
    roles: [Roles.Owner, Roles.Admin, Roles.ProjectManager],
    features: ["client_prospect_coding"],
  },
  {
    key: "timeentry",
    label: "Time",
    to: "/timeentry",
    icon: "/images/time-logo.svg",
    roles: [Roles.Owner, Roles.Admin, Roles.ProjectManager, Roles.User],
    features: ["time_entry"],
  },
  {
    key: "approvals",
    label: "Approvals",
    to: "/approvals",
    icon: "/images/approvals-logo.svg",
    roles: [Roles.Owner, Roles.Admin, Roles.ProjectManager, Roles.User],
    features: ["approval_workflow"],
    requiresApprover: true,
  },
  {
    key: "reports",
    label: "Reports",
    to: "/reports",
    icon: "/images/reports-logo.svg",
    roles: [Roles.Owner, Roles.Admin, Roles.ProjectManager, Roles.User],
  },
  {
    key: "settings",
    label: "Settings",
    to: "/settings",
    icon: "/images/settings.svg",
    roles: [Roles.Owner, Roles.Admin],
  },
];

/**
 * Menu-level visibility
 * Used in Layout.js
 */
export const canSeeNavItem = (
  roleId: number,
  item: NavItem,
  hasFeature?: (key: string) => boolean,
  approver?: boolean,
): boolean => {
  if (!roleId) return false;
  if (!item?.roles) return false;
  if (!item.roles.includes(roleId)) return false;
  if (hasFeature && item.features?.length) {
    if (!item.features.every(hasFeature)) return false;
  }
  if (item.requiresApprover && !approver) return false;
  return true;
};
